import React, { useState, useCallback, useMemo } from "react";
import { Message } from "semantic-ui-react";
import api from "../../../../../api";
import TestCallVariablesModal from "./TestCallVariablesModal";
import BotbuilderLoadingOverlay from "./BotbuilderLoadingOverlay";

/**
 * Laravel test-variable APIs expect zyra_workspace.auto_id (integer).
 * BotBuilder PHP sets custom_workspace_id to raw id for OUTBOUND only; other types use "CB_{auto_id}".
 * Route param :id is typically base64(auto_id) (see atob usage elsewhere in BotBuilderDetail).
 */
export function resolveWorkspaceTestVariableApiId({ customWorkspaceId, routeWorkspaceId }) {
  if (customWorkspaceId !== undefined && customWorkspaceId !== null && String(customWorkspaceId).trim() !== "") {
    const s = String(customWorkspaceId).trim();
    const cb = /^CB_(\d+)$/i.exec(s);
    if (cb) {
      const n = parseInt(cb[1], 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
    if (/^\d+$/.test(s)) {
      const n = parseInt(s, 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  if (routeWorkspaceId !== undefined && routeWorkspaceId !== null && String(routeWorkspaceId).trim() !== "") {
    try {
      const decoded = atob(String(routeWorkspaceId).trim());
      const n = parseInt(decoded, 10);
      if (Number.isFinite(n) && n > 0) return n;
    } catch (e) {
      /* route id may not be base64 */
    }
    const s = String(routeWorkspaceId).trim();
    if (/^\d+$/.test(s)) {
      const n = parseInt(s, 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return null;
}

function formatLoadErrorMessage(res) {
  if (!res || res.status !== false) return "";
  if (res.errors && typeof res.errors === "object") {
    const firstKey = Object.keys(res.errors)[0];
    const arr = firstKey ? res.errors[firstKey] : null;
    if (Array.isArray(arr) && arr[0]) return String(arr[0]);
  }
  if (res.message) return String(res.message);
  return "Could not load test variables. Please try again.";
}

const EDIT_LINK_STYLE = {
  border: "none",
  background: "transparent",
  padding: 0,
  fontSize: "13px",
  color: "#5867dd",
  textDecoration: "underline",
  fontWeight: 500,
  lineHeight: 1.2,
  marginLeft: "2px",
};

/**
 * Standalone test-variable editor (e.g. OUTBOUNDSMS under "Click to Test!").
 * Outbound voice continues to use VapiAssitant — keep that file unchanged to avoid regressions.
 *
 * @param {string|number} [props.customWorkspaceId] — API `custom_workspace_id` (often "CB_{auto_id}").
 * @param {string} [props.routeWorkspaceId] — URL segment `/botbuilder/:id` (often base64 auto_id).
 */
function WorkspaceTestVariablesEditor({ customWorkspaceId, routeWorkspaceId }) {
  const apiWorkspaceId = useMemo(
    () => resolveWorkspaceTestVariableApiId({ customWorkspaceId, routeWorkspaceId }),
    [customWorkspaceId, routeWorkspaceId]
  );

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("edit");
  const [variableNames, setVariableNames] = useState([]);
  const [initialValues, setInitialValues] = useState({});
  const [validationError, setValidationError] = useState("");
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const handleEditClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (apiWorkspaceId == null) {
        setFetchError("Workspace id is not available. Reload the page or save the workspace and try again.");
        return;
      }
      setFetchLoading(true);
      setFetchError("");
      api.botbuilder
        .getWorkspaceTestVariables({ workspace_id: apiWorkspaceId })
        .then((res) => {
          if (!res || res.status === false) {
            setFetchError(formatLoadErrorMessage(res));
            return;
          }
          const names = (res && res.data && res.data.variable_names) || [];
          const saved = (res && res.data && res.data.variables) || {};
          setVariableNames(names);
          setInitialValues({ ...saved });
          setModalMode("edit");
          setShowModal(true);
          setValidationError("");
          setFetchError("");
        })
        .catch(() => {
          setFetchError("Could not load test variables. Please try again.");
        })
        .finally(() => {
          setFetchLoading(false);
        });
    },
    [apiWorkspaceId]
  );

  const handleSave = useCallback(
    (values) => {
      const names = variableNames || [];
      if (apiWorkspaceId == null) {
        return;
      }
      if (names.length === 0) {
        setShowModal(false);
        setValidationError("");
        return;
      }
      const missing = names.some((n) => !String(values[n] || "").trim());
      if (missing) {
        setValidationError("Some variables are missing values");
        return;
      }
      setValidationError("");
      api.botbuilder
        .saveWorkspaceTestVariables({ workspace_id: apiWorkspaceId, variables: values })
        .then((res) => {
          if (res && res.status === false) {
            setValidationError("Could not save test variables. Please try again.");
            return;
          }
          setShowModal(false);
          setValidationError("");
        });
    },
    [apiWorkspaceId, variableNames]
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setValidationError("");
  }, []);

  if (apiWorkspaceId == null) {
    return null;
  }

  return (
    <>
      <BotbuilderLoadingOverlay active={fetchLoading} />
      <div className="yr-workspace-test-vars-editor">
      <TestCallVariablesModal
        open={showModal}
        mode={modalMode}
        variableNames={variableNames}
        initialValues={initialValues}
        onSave={handleSave}
        onCancel={handleCancel}
        validationError={validationError}
        onFieldChange={() => setValidationError("")}
      />
      <button
        type="button"
        onClick={handleEditClick}
        disabled={fetchLoading}
        style={{
          ...EDIT_LINK_STYLE,
          cursor: fetchLoading ? "not-allowed" : "pointer",
          opacity: fetchLoading ? 0.6 : 1,
        }}
      >
        Edit Variables
      </button>
      {fetchError ? (
        <Message
          negative
          size="small"
          onDismiss={() => setFetchError("")}
          style={{ marginTop: 4, marginBottom: 0, width: "100%", maxWidth: "100%" }}
        >
          {fetchError}
        </Message>
      ) : null}
      </div>
    </>
  );
}

export default WorkspaceTestVariablesEditor;
